import { useEffect, useMemo, useState } from 'react'

import cn from 'classnames'

import { SwapButton } from '@src/components/buttons/SwapButton'
import { ChainId } from '@src/configs/chainInfo'
import { APP } from '@src/configs/paths'
import { MAX_TOKEN_DISPLAY_DECIMALS } from '@src/constants/exchange'
import { StepNames } from '@src/enums'
import { LiquiditySource } from '@src/enums'
import { useSwapButtonState } from '@src/hooks/useSwapButtonState'
import { useWalletConnections } from '@src/hooks/useWalletConnections'
import { useExchangeDataContext } from '@src/pages/Exchange/context/ExchangeDataContext'
import { useExchangeState } from '@src/pages/Exchange/context/ExchangeStateContext'
import { IExchangeData } from '@src/types/exchange'
import { FeatherQuoteError } from '@src/types/feather'
import { IQuoteResponse } from '@src/types/order'
import { Address } from '@src/types/token'
import { Balance, isFeatureEnabled } from '@src/utils'
import { isFeatherChain } from '@src/utils/chainUtils'

interface SwapErrors {
    isInsufficientBalance: boolean
    isQuoteInvalid: boolean
    // add other booleans if needed
}

interface ISwapActionButtonProps {
    sellData: IExchangeData
    buyData: IExchangeData

    sellAmountString: string

    /** For your aggregator's quote result. */
    quoteResponse: IQuoteResponse

    errors: SwapErrors
    hasInsufficientGas: boolean

    /** Loading state for buy output */
    isBuyOutputLoading?: boolean

    /** If user toggled "custom wallet"? */
    isCustomRecipientWallet: boolean
    destinationAddress: Address | undefined

    /** If the user wants to do something on button click. */
    onConfirmExchange: () => void

    /** If the user wants to create a Feather order (Bitcoin destination) */
    onCreateFeatherOrder?: () => void

    /** If the user wants to retry fetching a Feather quote after an error. */
    onRetryFeatherQuote?: () => void

    /** Gas fee simulation status. */
    isGasEstimationFetched?: boolean
    isQuoteLoading?: boolean

    /** Error message from Feather quote fetch attempt */
    featherQuoteError?: FeatherQuoteError | null

    /** Liquidity check values */
    isLiquidityUnavailable?: boolean
    isCheckingLiquidity?: boolean
    liquidityError?: string | null
    liquiditySource?: LiquiditySource
}

/**
 * A wrapper for your <SwapButton> that sets disabled states,
 * passes in the needed props, etc.
 */
export function SwapActionButton({
    sellData,
    buyData,
    sellAmountString,
    quoteResponse,
    errors,
    hasInsufficientGas,
    destinationAddress,
    onConfirmExchange,
    onCreateFeatherOrder,
    onRetryFeatherQuote,
    isGasEstimationFetched,
    isQuoteLoading,
    isCustomRecipientWallet,
    featherQuoteError,
    isLiquidityUnavailable = false,
    isCheckingLiquidity = false,
    liquidityError = null,
    isBuyOutputLoading = false,
    liquiditySource,
}: ISwapActionButtonProps) {
    const { isTransactionCapped, transactionCapRemainingAllowance, transactionCapNextUnlock } = useExchangeState()
    const { transactionCapError, isLoadingTransactionCap, milesStatus } = useExchangeDataContext()
    const { isSolanaConnected, isTronConnected, isEVMConnected } = useWalletConnections()

    const isFeatherEnabled = isFeatureEnabled('ENABLE_FEATHER')

    // Check if the buy chain is a Feather chain
    const isFeatherOrder = useMemo(() => isFeatherEnabled && isFeatherChain(buyData.chainId), [buyData.chainId])

    // Check if input is required (no sell amount entered)
    const inputRequired = useMemo(
        () => Number(sellData?.value) === 0 && Number(buyData?.value || 0) === 0,
        [sellData, buyData]
    )

    // Check if the trade amount is below $25 minimum for Feather orders
    const isFeatherAmountBelowMinimum = useMemo(() => {
        // Only check for Feather orders
        if (!isFeatherOrder) return false

        // If no amount entered, don't show the specific error yet
        if (!sellData.value || sellData.value === BigInt(0)) return false

        // Check against minimum USD value if we have price data
        if (sellData.token.price_current && sellData.token.decimals) {
            const valueInUSD = (Number(sellData.value) * sellData.token.price_current) / 10 ** sellData.token.decimals
            return valueInUSD < 25
        }

        // Fallback to token amount check if no price data available
        const minimumTokenAmount = BigInt(Math.pow(10, Math.max(0, sellData.token.decimals - 1)))
        return sellData.value < minimumTokenAmount
    }, [isFeatherOrder, sellData.value, sellData.token])

    // Check if a destination address is required and missing
    const isMissingDestinationAddress = useMemo(() => {
        // Only check for Feather orders
        if (!isFeatherOrder) return false

        // If we have some sell value entered, require a destination address
        if (sellData.value && sellData.value > BigInt(0)) {
            return !destinationAddress || destinationAddress === ''
        }

        return false
    }, [isFeatherOrder, sellData.value, destinationAddress])

    // Don't show error during active loading or when input is being changed
    const shouldShowFeatherError = useMemo(() => {
        // If there's no error, don't show anything
        if (!featherQuoteError) return false

        // Don't show error if we're actively loading a quote
        if (isBuyOutputLoading || isQuoteLoading) return false

        // Don't show error during initial input (when user is typing)
        if (inputRequired || sellAmountString === '0') return false

        // Otherwise, show the error
        return true
    }, [featherQuoteError, isBuyOutputLoading, isQuoteLoading, inputRequired, sellAmountString])

    // Calculate if the sell and buy wallets are connected based on chain type
    const isSellChainWalletConnected = useMemo(() => {
        switch (sellData.chainId) {
            case ChainId.Solana:
                return isSolanaConnected
            case ChainId.Tron:
                return isTronConnected
            default:
                return isEVMConnected
        }
    }, [sellData.chainId, isSolanaConnected, isTronConnected, isEVMConnected])

    const isBuyChainWalletConnected = useMemo(() => {
        if (!buyData) return true // No buy data means no need for wallet connection

        switch (buyData.chainId) {
            case ChainId.Solana:
                return isSolanaConnected
            case ChainId.Tron:
                return isTronConnected
            default:
                return isEVMConnected
        }
    }, [buyData, isSolanaConnected, isTronConnected, isEVMConnected])

    // State to track the countdown timer
    const [timeRemaining, setTimeRemaining] = useState<{ hours: number; minutes: number; seconds: number } | null>(
        transactionCapNextUnlock
    )

    // Check if buy amount exceeds cap
    const buyAmountExceedsCap = useMemo(() => {
        if (transactionCapRemainingAllowance === null) {
            return false
        }

        // Use buy amount instead of sell amount for comparison
        const buyAmount = buyData.value
        return buyAmount > BigInt(transactionCapRemainingAllowance)
    }, [buyData.value, transactionCapRemainingAllowance])

    // Format the remaining transaction cap for display
    const formattedRemainingTransactionCap = useMemo(() => {
        if (transactionCapRemainingAllowance === null) {
            return `0 ${buyData.token.symbol}`
        }

        try {
            const capValue = BigInt(transactionCapRemainingAllowance)
            return `${new Balance(buyData.token)
                .setValue(capValue)
                .formatWithCommas(MAX_TOKEN_DISPLAY_DECIMALS)} ${buyData.token.symbol}`
        } catch (e) {
            return `0 ${buyData.token.symbol}`
        }
    }, [transactionCapRemainingAllowance, buyData.token])

    // Format the total transaction cap for display
    const formattedTransactionCap = useMemo(() => {
        const fmt = new Balance(buyData.token)
            .setValue(BigInt(`${transactionCapRemainingAllowance ?? 0}`))
            .formatWithCommas(MAX_TOKEN_DISPLAY_DECIMALS)
        return `${fmt} ${buyData.token.symbol}`
    }, [transactionCapRemainingAllowance, buyData.token])

    // Update the timer every second
    useEffect(() => {
        if (isFeatureEnabled('TRANSACTION_CAP')) {
            // Initialize with the value from context
            setTimeRemaining(transactionCapNextUnlock)

            // Only start the timer if we have a next unlock time
            if (!transactionCapNextUnlock) return

            const timer = setInterval(() => {
                setTimeRemaining(prev => {
                    if (!prev) return null

                    let { hours, minutes, seconds } = prev

                    // Decrement seconds
                    seconds--

                    // Handle minute rollover
                    if (seconds < 0) {
                        seconds = 59
                        minutes--
                    }

                    // Handle hour rollover
                    if (minutes < 0) {
                        minutes = 59
                        hours--
                    }

                    // Stop at zero
                    if (hours < 0) {
                        clearInterval(timer)
                        return null
                    }

                    return { hours, minutes, seconds }
                })
            }, 1000)

            return () => clearInterval(timer)
        }
    }, [transactionCapNextUnlock])

    // Format the time until next unlock using the live countdown
    const formatTimeUntilNextUnlock = () => {
        if (!timeRemaining) return ''

        const { hours, minutes, seconds } = timeRemaining
        const parts = []

        if (hours > 0) parts.push(`${hours}h`)
        if (minutes > 0) parts.push(`${minutes}m`)
        if (seconds > 0 && hours === 0) parts.push(`${seconds}s`) // Only show seconds if less than an hour

        return parts.join(' ')
    }

    // Get transaction cap button text
    const getCapButtonText = () => {
        // For Feather orders, first check if destination address is missing
        if (isFeatherEnabled && isFeatherOrder && isMissingDestinationAddress) {
            return 'Add Recipient Address'
        }

        // For Feather orders, check client-side minimum amount validation
        if (isFeatherEnabled && isFeatherOrder && isFeatherAmountBelowMinimum) {
            return 'Trade amount too small (min $25)'
        }

        // Then check backend-reported errors
        if (isFeatherEnabled && isFeatherOrder && shouldShowFeatherError) {
            // Return specific messages based on error type
            if (featherQuoteError?.type === 'amount_too_small') {
                return 'Trade amount too small (min $25)'
            } else if (featherQuoteError?.type === 'calculation_error') {
                return 'Cannot calculate quote'
            } else if (featherQuoteError?.type === 'missing_address') {
                return 'Add Recipient Address'
            }
            return 'Could not fetch quote'
        }

        // Normal Feather order text handling - now using the same text logic as regular swaps
        if (isFeatherEnabled && isFeatherOrder) {
            // First handle error states for Feather orders
            if (inputRequired || sellAmountString === '0') {
                return 'Enter an amount'
            }

            if (errors.isInsufficientBalance) {
                return `Insufficient ${sellData?.token?.symbol ?? ''} balance`
            }

            // For Bitcoin destinations specifically, check for recipient address
            if (isCustomRecipientWallet && (!destinationAddress || destinationAddress === '')) {
                return 'Add Recipient Address'
            }

            // Loading states
            if (isBuyOutputLoading || isQuoteLoading) {
                return 'Fetching quotes...'
            }

            // If all checks pass, show "Swap" button even for Feather orders to be consistent
            return 'Swap'
        }

        // If there's an error fetching the cap
        if (transactionCapError) {
            return 'Route Temporarily Disabled'
        }

        // If we're still loading the cap
        if (isLoadingTransactionCap) {
            return 'Checking Cap...'
        }

        // If the cap is reached
        if (isTransactionCapped && timeRemaining) {
            return `Route Disabled (${formatTimeUntilNextUnlock()})`
        }

        // If the amount exceeds the cap
        if (buyAmountExceedsCap && transactionCapRemainingAllowance !== null) {
            return `Exceeds daily limit (${formattedRemainingTransactionCap} left)`
        }

        // If we have cap information but no issues
        if (transactionCapRemainingAllowance !== null) {
            // Show the time until next unlock if available, even when there's still cap remaining
            if (timeRemaining) {
                return `Swap (${formattedRemainingTransactionCap} of daily limit left), resets in ${formatTimeUntilNextUnlock()}`
            }
            return `Swap (${formattedRemainingTransactionCap} of daily limit left)`
        }

        return '' // Return empty string to use the default text
    }

    const getMilesMessage = () => {
        if (milesStatus.requiresMiles) {
            if (milesStatus.isLoadingMiles) {
                return 'Checking Mach Miles requirement...'
            }
            if (milesStatus.error) {
                return `Error checking Mach Miles: ${milesStatus.error}`
            }
            if (!milesStatus.hasEnoughMiles) {
                return (
                    <span>
                        You need 1K Mach Miles to use MON/MEGA{' - '}
                        <a
                            href={APP}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='inline-flex items-center text-yellow-300 hover:text-yellow-200'
                            title='Visit Mach Exchange to earn Mach Miles'
                        >
                            <span className='underline'>Get Mach Miles</span>
                            <svg className='ml-1 h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                <path
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    strokeWidth={2}
                                    d='M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14'
                                />
                            </svg>
                        </a>
                    </span>
                )
            }
        }
        return null
    }

    // Get transaction cap message for display above the button
    const getTransactionCapMessage = () => {
        // If there's an error fetching the cap
        if (transactionCapError) {
            return `Route temporarily disabled: ${transactionCapError}`
        }

        // If we're still loading the cap
        if (isLoadingTransactionCap) {
            return 'Checking transaction cap...'
        }

        // If the cap is reached
        if (isTransactionCapped && timeRemaining) {
            return `Daily cap of ${formattedTransactionCap} reached. Next available in ${formatTimeUntilNextUnlock()}`
        }

        // If the amount exceeds the cap
        if (buyAmountExceedsCap && transactionCapRemainingAllowance !== null) {
            if (timeRemaining) {
                return `Amount exceeds remaining daily limit of ${formattedRemainingTransactionCap}. Full limit resets in ${formatTimeUntilNextUnlock()}.`
            }
            return `Amount exceeds remaining daily limit of ${formattedRemainingTransactionCap}. Route limited.`
        }

        // If there's still some cap remaining but not full, show when it will reset
        if (transactionCapRemainingAllowance !== null && timeRemaining) {
            return `Daily limit usage: ${formattedRemainingTransactionCap} remaining. Full limit resets in ${formatTimeUntilNextUnlock()}.`
        }

        // Default message
        if (isTransactionCapped) {
            return 'This route is temporarily disabled'
        }

        return null
    }

    // Determine if there's a cap-related issue
    const hasCapIssue =
        isFeatureEnabled('TRANSACTION_CAP') && (isTransactionCapped || buyAmountExceedsCap || !!transactionCapError)

    // Determine if there's cap-related info to show (including non-error info)
    const hasCapInfo = hasCapIssue || (transactionCapRemainingAllowance !== null && timeRemaining !== null)

    // Use our shared hook for button state calculation
    const { buttonDisabled } = useSwapButtonState({
        sellData,
        buyData,
        sellAmountString,
        isInsufficientBalance: errors.isInsufficientBalance,
        hasInsufficientGas,
        isBuyOutputLoading,
        isQuoteInvalid: errors.isQuoteInvalid,
        isCustomRecipientWallet,
        destinationAddress,
        quoteResponse,
        isGasEstimationFetched,
        isQuoteLoading,
        isBuyChainWalletConnected,
        isSellChainWalletConnected,
        isLiquidityUnavailable,
        isCheckingLiquidity,
        liquidityError,
        isTransactionCapped: isFeatureEnabled('TRANSACTION_CAP') && isTransactionCapped,
        capButtonText: getCapButtonText(),
    })

    // Override disabled state to include transaction cap-specific conditions
    const finalDisabled =
        buttonDisabled ||
        (isFeatherEnabled && isFeatherOrder && shouldShowFeatherError) || // Disable for visible Feather errors
        (isFeatherEnabled && isFeatherOrder && isFeatherAmountBelowMinimum) || // Client-side min amount validation
        (isFeatherEnabled && isFeatherOrder && isMissingDestinationAddress) || // Missing destination address
        (isFeatureEnabled('TRANSACTION_CAP') && buyAmountExceedsCap) ||
        (isFeatureEnabled('TRANSACTION_CAP') && !!transactionCapError)

    // Handle click based on whether it's a Feather order or regular order
    const handleButtonClick = () => {
        // For Feather orders, check that all requirements are met
        if (isFeatherEnabled && isFeatherOrder) {
            // Don't proceed if destination address is missing
            if (isMissingDestinationAddress) {
                return
            }

            // Don't proceed if amount is below minimum
            if (isFeatherAmountBelowMinimum) {
                return
            }

            // Call the Feather order handler if all requirements are met
            if (onCreateFeatherOrder) {
                onCreateFeatherOrder()
            }
        } else {
            // For regular orders, just call the normal handler
            onConfirmExchange()
        }
    }

    return (
        <>
            {isFeatureEnabled('TRANSACTION_CAP') && hasCapInfo && (
                <div
                    className={`mb-2 text-center text-sm ${
                        transactionCapError
                            ? 'text-yellow-500'
                            : isTransactionCapped || buyAmountExceedsCap
                              ? 'text-red-500'
                              : 'text-blue-500'
                    }`}
                >
                    {getTransactionCapMessage()}
                </div>
            )}
            {milesStatus.requiresMiles && (
                <div className='mb-2 text-center text-sm text-yellow-300'>{getMilesMessage()}</div>
            )}

            <SwapButton
                className={cn('w-full')}
                onClick={handleButtonClick}
                disabled={finalDisabled}
                errorsJSON={{
                    isInsufficientBalance: errors.isInsufficientBalance,
                    isQuoteInvalid: errors.isQuoteInvalid,
                    isBuyOutputLoading: isQuoteLoading && Number(sellData.value) > 0, // Using isQuoteLoading instead of isBuyOutputLoading
                    hasInsufficientGas,
                    isLiquidityUnavailable,
                    isTransactionCapped: isFeatureEnabled('TRANSACTION_CAP') && hasCapIssue, // Use the boolean hasCapIssue flag
                }}
                hasInsufficientGas={hasInsufficientGas}
                sellData={sellData}
                buyData={buyData}
                sellAmountString={sellAmountString}
                currentStep={StepNames.UNSTARTED}
                quoteResponse={quoteResponse}
                isCustomRecipientWallet={isCustomRecipientWallet}
                destinationAddress={destinationAddress}
                isGasEstimationFetched={isGasEstimationFetched}
                isQuoteLoading={isQuoteLoading}
                buttonText={getCapButtonText() || undefined} // Pass cap button text or let SwapButton handle it
                liquidityAvailable={!isLiquidityUnavailable}
                isCheckingLiquidity={isCheckingLiquidity}
                liquidityError={liquidityError}
                liquiditySource={liquiditySource}
            />
        </>
    )
}
